import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Scan, Clock, CheckCircle, AlertCircle, XCircle, ExternalLink, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "wouter";

interface ScanResult {
  id: number;
  url: string;
  type: string;
  status: string;
  results?: string;
  createdAt: string;
  updatedAt: string;
}

export function RecentScans() {
  const { data: scans = [], isLoading } = useQuery<ScanResult[]>({
    queryKey: ["/api/scans"],
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-purple-500" />;
      case "running":
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: "default",
      running: "secondary",
      failed: "destructive",
      pending: "outline",
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || "outline"}>
        {status}
      </Badge>
    );
  };

  const getScanTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      full: "Full Security",
      javascript: "JS Integrity",
      quick: "Quick Scan",
      crawl: "Single crawl",
    };
    return labels[type] || type;
  };

  const getComplianceScore = (results: string | undefined) => {
    if (!results) return null;
    try {
      const parsed = JSON.parse(results);
      return parsed.complianceScore;
    } catch {
      return null;
    }
  };

  const getVulnerabilityCount = (results: string | undefined) => {
    if (!results) return null;
    try {
      const parsed = JSON.parse(results);
      return parsed.vulnerabilities?.length || 0;
    } catch {
      return null;
    }
  };

  const getDiscoveredCount = (results: string | undefined) => {
    if (!results) return null;
    try {
      const parsed = JSON.parse(results);
      return parsed.discoveredCount ?? parsed.discoveredUrls?.length ?? null;
    } catch {
      return null;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scan className="h-5 w-5" />
            Recent Scans
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 dark:bg-slate-800/50 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-4 w-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
                  <div className="space-y-1">
                    <div className="h-4 w-48 bg-slate-300 dark:bg-slate-600 rounded"></div>
                    <div className="h-3 w-24 bg-slate-300 dark:bg-slate-600 rounded"></div>
                  </div>
                </div>
                <div className="h-6 w-16 bg-slate-300 dark:bg-slate-600 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const recentScans = scans.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scan className="h-5 w-5" />
          Recent Scans
          {scans.length > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {scans.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {recentScans.length === 0 ? (
          <div className="text-center py-8">
            <Scan className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No scans yet
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Start your first security scan to monitor your website's security posture.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentScans.map((scan) => {
              const complianceScore = getComplianceScore(scan.results);
              const vulnerabilityCount = getVulnerabilityCount(scan.results);
              const discoveredCount = getDiscoveredCount(scan.results);

              return (
                <Link key={scan.id} href={`/scan/${scan.id}`}>
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-3 flex-1">
                      {getStatusIcon(scan.status)}
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm group-hover:text-purple-600 transition-colors">{scan.url}</span>
                          <Badge variant="outline" className="text-xs">
                            {getScanTypeLabel(scan.type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
                          <span>
                            {formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true })}
                          </span>
                          {complianceScore !== null && (
                            <span className="flex items-center gap-1">
                              Score: {complianceScore}%
                            </span>
                          )}
                          {vulnerabilityCount !== null && (
                            <span className="flex items-center gap-1">
                              {vulnerabilityCount} vulnerabilities
                            </span>
                          )}
                          {scan.type === "crawl" && discoveredCount !== null && (
                            <span className="flex items-center gap-1">
                              {discoveredCount} URLs in domain
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(scan.status)}
                      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-purple-600 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}